import Image from 'next/image';
import { gql } from '@apollo/client';

import { NextLink, Text } from '@/atoms';
import { Prose } from '@/molecules';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  imagePartial,
  pagePropsPartial,
  sliderHero,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import APServer from '@/lib/graphql/serverClient';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import { removeBaseURL } from '@/lib/utils';

import type { TypeImageWordpress } from '@/components/types';
import type { GetStaticPropsResult } from 'next';
import type { NextPageWithLayout } from 'pages/_app';
import type { ElementType } from 'react';

interface Marcas {
  initialProps: {
    menu: any;
    options: any;
  };
  page: {
    hero: any;
    flexibleContent: {
      flexible: any[];
    };
    brands: [string, { title: string; url: string }[]][];
    listOfBrands: {
      brands: {
        title?: string;
        description?: string;
        image?: TypeImageWordpress;
        headingType: ElementType;
        desktopImageAlign: 'left' | 'right';
        mobileImageAlign: 'top' | 'bottom';
      };
      mainBrands: {
        brands: {
          image: TypeImageWordpress;
          link: { url: string };
        }[];
        description?: string;
        title?: string;
        headingType: ElementType;
      };
    };
  };
}

const Marcas: NextPageWithLayout<Marcas> = (props) => (
  <>
    <HeroSlider hero={props.page.hero} />

    {(props.page?.listOfBrands?.brands?.title ||
      props.page?.listOfBrands?.brands?.description ||
      props.page?.listOfBrands?.brands?.image) && (
      <div className="u-wrapper flex flex-col gap-y-2 px-4 py-4 md:py-8">
        {props.page.listOfBrands.brands?.title && (
          <Text as={props.page.listOfBrands.brands.headingType}>
            {props.page.listOfBrands.brands.title}
          </Text>
        )}
        <div
          className={`grid grid-cols-1 gap-4 ${props.page.listOfBrands.brands.image ? 'md:grid-cols-[1fr_1fr]' : ''}`}
        >
          {props.page.listOfBrands.brands?.description && (
            <Prose
              html={props.page.listOfBrands.brands.description}
              className="w-full"
            />
          )}

          {props.page.listOfBrands.brands.image && (
            <Image
              src={props.page.listOfBrands.brands.image.sourceUrl}
              alt={props.page.listOfBrands.brands.image.altText}
              width={props.page.listOfBrands.brands.image.mediaDetails.width}
              height={props.page.listOfBrands.brands.image.mediaDetails.height}
              className={`object-contain ${props.page.listOfBrands.brands.desktopImageAlign === 'left' ? 'order-first' : ''} ${props.page.listOfBrands.brands.mobileImageAlign === 'top' ? 'max-md:order-first' : ''}`}
            />
          )}
        </div>
      </div>
    )}

    {(props.page?.listOfBrands?.mainBrands?.title ||
      props.page?.listOfBrands?.mainBrands?.description ||
      props.page?.listOfBrands?.mainBrands?.brands?.length > 0) && (
      <div className="u-wrapper space-y-4 px-4 py-4 md:py-8">
        {props.page.listOfBrands.mainBrands?.title && (
          <Text as={props.page.listOfBrands.mainBrands.headingType}>
            {props.page.listOfBrands.mainBrands.title}
          </Text>
        )}

        {props.page.listOfBrands.mainBrands?.description && (
          <Prose html={props.page.listOfBrands.mainBrands.description} />
        )}

        <div className="grid grid-cols-[repeat(auto-fit,_100px)] gap-4">
          {props.page.listOfBrands.mainBrands?.brands?.map((b, i) => (
            <NextLink key={i} href={removeBaseURL(b.link.url ?? '/')}>
              <a>
                <Image
                  src={b.image.sourceUrl}
                  alt={b.image.altText}
                  width={100}
                  height={100}
                  className="object-cover"
                />
              </a>
            </NextLink>
          ))}
        </div>
      </div>
    )}

    <div className="u-wrapper space-y-8 px-4 py-8">
      <h1>Listado de marcas</h1>
      <div className="mx-auto grid grid-cols-[repeat(auto-fit,_minmax(40px,_1fr))] justify-center md:grid-cols-[repeat(auto-fit,_40px)]">
        {props.page.brands.map(([letter], i) => (
          <NextLink href={`/marcas#${letter}`} key={i}>
            <a className="flex h-10 w-10 items-center justify-center hover:bg-bg-tertiary hover:text-typo-alternative">
              {letter}
            </a>
          </NextLink>
        ))}
      </div>

      {props.page.brands.map(([letter, brands]) => (
        <div id={letter} key={letter} className="space-y-2">
          <p className="u-headline u-headline--h1 max-md:my-4">{letter}</p>

          <div
            style={{
              gridTemplateRows: `repeat(${brands.length > 4 ? Math.ceil(brands.length / 4) : brands.length}, auto)`,
            }}
            className="grid grid-flow-row grid-cols-1 gap-x-6 gap-y-2 md:grid-flow-col md:grid-cols-4"
          >
            {brands.map((brand, i) => (
              <NextLink key={i} href={brand.url}>
                <a className="u-body u-body--s w-fit uppercase text-typo-primary hover:underline hover:underline-offset-2">
                  {brand.title}
                </a>
              </NextLink>
            ))}
          </div>
        </div>
      ))}
    </div>

    <FlexibleContent
      type="Page"
      flexible={props.page.flexibleContent.flexible}
    />
  </>
);

const BRANDS = gql`
  query getAllBrands($pageSize: Int!, $cursor: String) {
    marcas(
      first: $pageSize
      after: $cursor
      where: { orderby: { order: ASC, field: TITLE } }
    ) {
      edges {
        node {
          title
          slug
          seo {
            metaRobotsNoindex
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

interface Brands {
  marcas: {
    edges: {
      node: {
        title: string;
        slug: string;
        seo: {
          metaRobotsNoindex: 'noindex' | 'index';
        };
      };
    }[];
    pageInfo: {
      endCursor: string;
      hasNextPage: boolean;
    };
  };
}

export async function getStaticProps(): Promise<GetStaticPropsResult<Marcas>> {
  const hero = `
    page(id: $slug, idType: URI) {
      flexibleContent: ${FlexibleContentBlocks('Page')}
      hero: ${sliderHero('Page')}
      listOfBrands{
        brands {
          title
          description
          image ${imagePartial}
          headingType
          desktopImageAlign
          mobileImageAlign
        }
        mainBrands {
          title
          headingType
          description
          brands {
            image ${imagePartial}
              link {
                url
              }
          }
        }
      }
    }
  `;
  const Query = PageQuery(pagePropsPartial, hero);

  const data = await pagePropsServerTransformer({
    staticProps: await getInitialProps(Query, { slug: 'marcas' }),
    type: 'Page',
    path: '/marcas',
  });

  if (data.notFound) {
    return {
      notFound: true,
    };
  }

  // We fetch all the brands.

  let hasNextPage = false;
  let cursor = '';
  const brands = new Map<string, { title: string; url: string }[]>();

  const isLetter = new RegExp(/[a-zA-Z]/);

  do {
    const { data } = await APServer().query<Brands>({
      query: BRANDS,
      variables: {
        pageSize: 100,
        cursor: cursor,
      },
    });

    for (const brand of data.marcas.edges) {
      // We check only in production because in pre we cannot index a brand.
      if (
        process.env.NEXT_PUBLIC_BACK_URL === 'https://admin.bigcrafters.com' &&
        brand.node.seo.metaRobotsNoindex === 'noindex'
      ) {
        continue;
      }

      const titleFirstChar = brand.node.title
        .charAt(0)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

      if (isLetter.exec(titleFirstChar)) {
        if (brands.has(titleFirstChar.toUpperCase())) {
          const temp = brands.get(titleFirstChar.toUpperCase());
          temp!.push({
            title: brand.node.title,
            url: routes.brand(brand.node.slug),
          });
        } else {
          brands.set(titleFirstChar.toUpperCase(), [
            {
              title: brand.node.title,
              url: routes.brand(brand.node.slug),
            },
          ]);
        }
      } else {
        if (brands.has('#')) {
          const temp = brands.get('#');
          temp!.push({
            title: brand.node.title,
            url: routes.brand(brand!.node.slug),
          });
        } else {
          brands.set('#', [
            {
              title: brand.node.title,
              url: routes.brand(brand.node.slug),
            },
          ]);
        }
      }
    }

    hasNextPage = data.marcas.pageInfo.hasNextPage;
    cursor = data.marcas.pageInfo.endCursor;
  } while (hasNextPage);

  return {
    ...data,
    props: {
      ...data.props,
      page: {
        ...data.props.page,
        brands: Array.from(brands),
      },
    },
  };
}

Marcas.getLayout = getBaseLayout;

export default Marcas;
